<template>
  <section class="digilocker-complete-loading">
    <div class="loader-card">
      <i class="ri-loader-4-line spinner" aria-hidden="true"></i>
      <p class="loader-title">Verifying DigiLocker session...</p>
      <p class="loader-subtitle">{{ subtitle }}</p>

      <button
        v-if="showOpenButton"
        type="button"
        class="open-btn"
        @click="openInPopupManually"
      >
        Open DigiLocker Popup
      </button>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const subtitle = ref("Preparing your secure verification window...");
const showOpenButton = ref(false);
let pendingAuthorizationUrl = "";

function isPopupWindowContext() {
  return typeof window !== "undefined" && !!window.opener && window.opener !== window;
}

function closePopupWindow() {
  if (!isPopupWindowContext()) {
    return false;
  }

  subtitle.value = "Verification finished. Closing this window...";
  window.setTimeout(() => {
    window.close();
  }, 150);
  return true;
}

function redirectToAuthorizationUrl() {
  if (!pendingAuthorizationUrl) {
    if (closePopupWindow()) {
      return;
    }

    subtitle.value = "Authorization URL is missing. Redirecting back...";
    window.setTimeout(() => {
      router.replace({
        name: "register",
        query: {
          role: "distributor",
        },
      });
    }, 900);
    return;
  }

  subtitle.value = "Redirecting to DigiLocker...";
  window.location.assign(pendingAuthorizationUrl);
}

function openInPopupManually() {
  redirectToAuthorizationUrl();
}

onMounted(() => {
  const payloadKey = String(route.query.payloadKey || "").trim();
  if (!payloadKey) {
    if (closePopupWindow()) {
      return;
    }

    subtitle.value =
      "Session key is missing. Redirecting back to registration...";
    window.setTimeout(() => {
      router.replace({
        name: "register",
        query: {
          role: "distributor",
        },
      });
    }, 900);
    return;
  }

  console.log("digilocker payloadKey:", payloadKey);


  const raw = window.localStorage.getItem(payloadKey);
  window.localStorage.removeItem(payloadKey);

  if (!raw) {
    if (closePopupWindow()) {
      return;
    }

    subtitle.value = "Session not found. Redirecting back to registration...";
    window.setTimeout(() => {
      router.replace({
        name: "register",
        query: {
          role: "distributor",
        },
      });
    }, 900);
    return;
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    parsed = null;
  }

  if (!parsed || typeof parsed !== "object") {
    if (closePopupWindow()) {
      return;
    }

    subtitle.value = "Invalid session data. Redirecting back...";
    window.setTimeout(() => {
      router.replace({
        name: "register",
        query: {
          role: "distributor",
        },
      });
    }, 900);
    return;
  }

  const authorizationUrl = String(
    parsed?.authorizationUrl ||
      parsed?.responseData?.authorizationUrl ||
      parsed?.responseData?.data?.authorizationUrl ||
      "",
  ).trim();

  pendingAuthorizationUrl = authorizationUrl;
  showOpenButton.value = false;
  redirectToAuthorizationUrl();

  
});
</script>

<style scoped>
.digilocker-complete-loading {
  min-height: calc(100vh - 140px);
  display: grid;
  place-items: center;
  padding: 24px;
}

.loader-card {
  width: min(420px, 100%);
  text-align: center;
  background: #ffffff;
  border: 1px solid rgba(0, 61, 165, 0.12);
  border-radius: 16px;
  padding: 28px 22px;
  box-shadow: 0 12px 32px rgba(10, 34, 64, 0.08);
}

.spinner {
  font-size: 44px;
  color: #003da5;
  display: inline-block;
  animation: spin 0.9s linear infinite;
}

.loader-title {
  margin: 14px 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: #0a2240;
}

.loader-subtitle {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #5f6f85;
}

.open-btn {
  margin-top: 14px;
  border: none;
  background: #003da5;
  color: #ffffff;
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.open-btn:hover {
  background: #00339a;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
